(function MediaHubPicker($, Adzuki)
{
	'use strict';

	/**
	 * The redesigned Maxx Media picker (M4), registered in one place for every host: the iframe
	 * picker (mediahub_cp.js) and Trip Summary's inline picker (its form apps, MEDIA-288). Each
	 * override keeps the shared component's logic and swaps in the redesign's template, so the
	 * page must render the MediaHubComponents, MediaHubAdd and MediaHubPicker elements.
	 * Hosts that don't call this (the Erez inline pickers, until MEDIA-304) keep the old picker.
	 */
	function withTemplate(base, template, extra) {
		return Object.assign({}, base, { template: template }, extra || {});
	}

	// Filters panel (MEDIA-284)
	function searchFilters() {
		const base = Adzuki.VueComponents.MediaHubSearchFilter;

		return withTemplate(base, '#mediahub-search-filters-picker', {
			computed: Object.assign({}, base.computed, {
				// filters changed from their defaults, for the Filters button
				activeFilterCount() {
					return [
						this.selectedInTags.length > 0,
						this.selectedExTags.length > 0,
						this.contentTypes.length > 0 || this.fileTypes.length > 0,
						this.showFavorites,
						this.showUntagged,
						this.showGlobalFiles && this.$franchiseId !== '1',
						this.showFranchise,
						this.showStatuses.length > 0,
						this.showSections.length > 0
					].filter(Boolean).length;
				}
			})
		});
	}

	// Cards, selection, bulk bar and footer (MEDIA-284, MEDIA-285)
	function selectFiles() {
		const base = Adzuki.VueComponents.AdzukiSelectFiles;

		return withTemplate(base, '#adzuki-select-files-picker', {
			emits: (base.emits || []).concat(['scope-change', 'favorite-change']),
			props: Object.assign({}, base.props, {
				scope: {
					type   : String,
					default: null
				},
				scopeBreakdown: {
					type   : Object,
					default: null
				},
				showScope: {
					type   : Boolean,
					default: false
				}
			}),
			data() {
				return Object.assign(base.data ? base.data.call(this) : {}, {
					pickerVisibility: {
						showGlobalFiles: false,
						showFranchise  : false
					}
				});
			},
			watch: Object.assign({}, base.watch, {
				// a favorite is listed on Favorites and on its own side, as two different objects,
				// so point the selection at this list's copies or it could be picked twice (MEDIA-310)
				media(items) {
					if (!this.actionSelect.length || !Array.isArray(items)) {
						return;
					}

					this.actionSelect = this.actionSelect.map((picked) => items.find((item) => item.id === picked.id) || picked);
				}
			}),
			methods: Object.assign({}, base.methods, {
				// the base toggle, plus telling the host so the Favorites tab and count follow (MEDIA-310)
				async saveFavorite(media) {
					const set = media.favorite === '1' ? 0 : 1;

					try {
						const response = await fetch(`${this.$baseUri}/Media/updateFavorite/${media.id}/${set}`);

						if (!response.ok) {
							throw new Error(response.statusText);
						}

						const data = await response.json();

						if (!data || data.status === 'error') {
							throw new Error(data && data.message ? data.message : 'No response from server');
						}

						media.favorite = String(set);

						this.openToast('success', `Media Item ${set === 1 ? '' : 'un'}favorited successfully`);
						this.$emit('favorite-change', set);
					} catch (error) {
						this.openToast('error', 'Error Updating Media Item: ' + error.message);
					}
				},
				// the list view's Uploaded column: mm/dd/yyyy, read off the stored date so no timezone shifts it
				pickerShortDate(value) {
					const parts = (/^(\d{4})-(\d{2})-(\d{2})/).exec(value || '');

					return parts ? parts[2] + '/' + parts[3] + '/' + parts[1] : (value || '');
				},
				pickerStatusShort(status) {
					const labels = {
						restricted: 'Limited',
						pending   : 'Pending',
						approved  : 'Unlimited'
					};

					return labels[status] || status;
				}
			}),
			computed: Object.assign({}, base.computed, {
				// the bulk button reads by caller; what it does is the same
				pickerVerb() {
					if (this.sectionInfo.internal_name === 'wysiwyg') {
						return 'Insert';
					}

					return Number(this.sectionInfo.is_attachment) === 1 ? 'Attach' : 'Use';
				},
				pickerLimit() {
					return Number(this.sectionInfo.upload_limit) || 0;
				},
				pickerSlotsLeft() {
					return Math.max(0, this.pickerLimit - this.actionSelect.length);
				},
				pickerSlotsText() {
					if (this.pickerLimit === 1) {
						return 'This field takes one file';
					}

					const picked = this.actionSelect.length ? ' · ' + this.actionSelect.length + ' picked' : '';

					return this.pickerSlotsLeft + ' of ' + this.pickerLimit + ' slots left' + picked;
				},
				pickerSelectionHint() {
					if (this.pickerSlotsLeft === 0) {
						return 'That’s every remaining slot on this field.';
					}

					return this.pickerSlotsLeft + ' more can be added';
				},
				pickerCapTitle() {
					return 'You may only upload ' + this.pickerLimit + ' files. Deselect one to choose this.';
				},
				// approval status only matters where the section needs approval
				pickerShowsStatus() {
					return Number(this.sectionInfo.needs_approval) === 1;
				},
				// the agent's own favorites that fit this caller; the Include switches don't apply (MEDIA-310)
				pickerFavoritesCount() {
					const favorites = this.scopeBreakdown && this.scopeBreakdown.favorites;

					return favorites ? (favorites.own || 0) : 0;
				},
				// each side's count, following the Include Global / Include Shared switches
				visibleScopeCounts() {
					if (!this.scopeBreakdown) {
						return null;
					}

					const counts = {};

					['library', 'attachments'].forEach((side) => {
						const split = this.scopeBreakdown[side] || {};

						counts[side] = (split.own || 0)
							+ (this.pickerVisibility.showFranchise ? (split.shared || 0) : 0)
							+ (this.pickerVisibility.showGlobalFiles ? (split.global || 0) : 0);
					});

					return counts;
				}
			})
		});
	}

	/**
	 * A heart was toggled in the picker (MEDIA-310). Keeps the Favorites count in step, and on
	 * the Favorites tab reloads it without the unfavorited file, moving back to the caller's
	 * tab when none are left. Both hosts call it with themselves: they share scope,
	 * scopeBreakdown, initScope() and getMedia().
	 */
	function favoriteChanged(host, set) {
		const favorites = host.scopeBreakdown && host.scopeBreakdown.favorites;

		if (!favorites) {
			return;
		}

		favorites.own = Math.max(0, (favorites.own || 0) + (set ? 1 : -1));

		if (host.scope !== 'favorites' || set) {
			return;
		}

		if (favorites.own === 0) {
			host.initScope();
			host.loading = true;
		}

		host.getMedia();
	}

	// Trip Summary's inline picker (MEDIA-288): the shared wrapper's terms gate and loading,
	// in the redesign's modal shell, with Library | Attachments passed through to the picker
	function mediaHubModal() {
		const base = Adzuki.VueComponents.AdzukiMediaHub;

		return withTemplate(base, '#adzuki-media-hub-picker', {
			data() {
				return Object.assign(base.data.call(this), {
					loading       : true,
					scope         : null,
					scopeBreakdown: null,
					showScope     : String(Adzuki.franchise_id) !== '1'
				});
			},
			methods: Object.assign({}, base.methods, {
				// the side the picker opens on comes from the caller's section, so load that first
				async loadPicker() {
					this.loading = true;
					await this.getSectionInfo();
					this.initScope();
					await this.getMedia();
				},
				initScope() {
					if (!this.showScope) {
						this.scope = null;
						return;
					}

					this.scope = Number(this.sectionInfo && this.sectionInfo.is_attachment) === 1 ? 'attachments' : 'library';
				},
				changeScope(scope) {
					if (!this.showScope || scope === this.scope) {
						return;
					}

					this.scope   = scope;
					this.loading = true;
					this.getMedia();
				},
				favoriteChanged(set) {
					favoriteChanged(this, set);
				},
				async getMedia() {
					const url = `${this.baseUri}/Media/getMediaItems/${this.section}`
						+ (this.scope ? '/scope:' + this.scope : '');

					try {
						const response = await Adzuki.Ajax.get(url);

						if (!response || response.code === 'tos_required') {
							throw new Error(response && response.message ? response.message : 'No response from server');
						}

						const items = this.scope ? (response.media || []) : response;

						items.forEach((item) => {
							if (item.type === 'video' && (item.duration === null || item.duration === undefined)) {
								item.duration = 0;
							}
						});

						this.scopeBreakdown   = this.scope ? (response.count_breakdown || null) : null;
						this.media            = items;
						this.mediaProps.media = items;

						Adzuki.MediaUrls.resolveAll(this.media).catch((error) => {
							console.error('Error loading media URLs:', error);
						});
					} catch (error) {
						Adzuki.StatusMessage('error', `Error retrieving media items: ${error.message}`);
					} finally {
						this.loading = false;
					}
				}
			})
		});
	}

	Adzuki.MediaHubPicker = {
		favoriteChanged: favoriteChanged,
		// the redesigned adzuki-select-files on its own, for a host that embeds the
		// picker in its own shell (Trip Summary's Change Photo, MEDIA-334)
		selectFiles    : selectFiles,
		register(app) {
			app.component('mediahub-search-filters', searchFilters());
			app.component('adzuki-select-files', selectFiles());
			app.component('mediahub-details-component', withTemplate(Adzuki.VueComponents.MediaHubDetails, '#mediahub-details-component-picker'));
			// the Upload tab uses Add Media's templates from MediaHubAddElement (MEDIA-287)
			app.component('adzuki-file-upload', withTemplate(Adzuki.VueComponents.AdzukiFileUpload, '#adzuki-file-upload-redesign'));
			app.component('adzuki-file-upload-form', withTemplate(Adzuki.VueComponents.AdzukiFileUploadForm, '#adzuki-file-upload-form-redesign'));
			app.component('adzuki-video-recorder', withTemplate(Adzuki.VueComponents.AdzukiVideoRecorder, '#adzuki-video-recorder-redesign'));
			app.component('adzuki-pexels', withTemplate(Adzuki.VueComponents.AdzukiPexels, '#adzuki-pexels-redesign'));
			app.component('adzuki-existing-file', withTemplate(Adzuki.VueComponents.AdzukiExistingFile, '#adzuki-existing-file-redesign'));
			app.component('adzuki-media-hub', mediaHubModal());
		}
	};
}(jQuery, window.Adzuki || {}));
